import * as React from 'react';
import debounce from 'lodash/debounce';
import { numberToPrettyString } from '@cian/utils';
import { mergeStyles } from '@cian/utils/lib/shared/style';
import { IState, IUserMenuProps } from './types';
import { Popover } from '../Popover';
import { ELkType, IHeaderV1Schema } from '../../repositories/header/entities/response/HeaderV1Schema';
import { OptionalLink } from '../OptionalLink';
import { CountView } from '../CountView';
import { BalanceIcon } from '../Icons';
import { trackProfessionalSearchClick } from './tracking';

export const DEFAULT_AVATAR = 'https://www.cian.ru/img/cian/icon_old/100x100.gif';

// хак для вычисления место показа поповера, значения взяты из /src/style.css .c-header-container
export const MIN_PADDING = 24;

export interface IMenuLink {
  key: string;
  title: string;
  icon?: JSX.Element;
  href?: string | null;
  availableLkTypes?: (ELkType.Homeowner | ELkType.Specialist | ELkType.Searcher)[] | null;
  className?: string;
  disable?: boolean;
  clickCallback?: () => void;
}

export class UserMenu extends React.Component<IUserMenuProps, IState> {
  public constructor(props: IUserMenuProps) {
    super(props);

    this.state = {
      isOpen: false,
      isFirstRender: true,
    };
  }

  public componentDidMount() {
    this.setState({
      isFirstRender: false,
    });
  }

  public render() {
    const { headerData } = this.props;
    const { isOpen } = this.state;

    if (headerData) {
      const { isAuthenticated, avatarUrl, firstName, lastName, lkUrl } = headerData;
      if (!isAuthenticated) {
        return undefined;
      }

      const popoverCssClassName = `c-header-user-menu-panel${isOpen ? '' : ' hidden'}`;
      const firstNameFirst = firstName && firstName.length > 0 && firstName[0];
      const lastNameFirst = lastName && lastName.length > 0 && lastName[0];

      const userName =
        (firstNameFirst && lastNameFirst && firstNameFirst + lastNameFirst) ||
        (firstNameFirst && firstNameFirst) ||
        (lastNameFirst && lastNameFirst) ||
        undefined;

      const urlAvatar = headerData && !headerData.avatarUrl && !userName ? DEFAULT_AVATAR : avatarUrl;
      const popoverStyles = this.calcPopoverStyles();

      return (
        <div className="c-header-user-menu-container" onMouseLeave={this.debouncedHandleHide}>
          <div className="c-header-user-menu" onMouseEnter={this.debouncedHandleShow}>
            <a href={lkUrl || '#'} className="avatar-container">
              {urlAvatar && <img className="user-avatar" src={urlAvatar} />}
              {!urlAvatar && userName && <div className="user-names">{userName}</div>}
            </a>
          </div>
          <Popover
            isActive={isOpen}
            onClickOutside={this.debouncedOutSideClick}
            cssClass={popoverCssClassName}
            cssClassContainer="c-header-user-menu-panel-container"
            style={popoverStyles}
          >
            {this.renderUserMenu()}
          </Popover>
        </div>
      );
    }

    return <div className="c-header-user-menu-container" />;
  }

  private calcPopoverStyles = () => {
    const { isFirstRender } = this.state;
    let style = {};

    if (!isFirstRender) {
      const headerContainer: HTMLDivElement | null = document.querySelector('div.c-header-container');
      if (headerContainer) {
        const rect = headerContainer.getBoundingClientRect();

        if (rect.left > 0) {
          style = {
            ...style,
            right: rect.left + MIN_PADDING,
          };
        }
      }
    }

    return style;
  };

  private handleLoginClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (window._headerTrackingEvent) {
      window._headerTrackingEvent('login');
    }
    if (window.waitForLoginPopup) {
      window.waitForLoginPopup();
    }

    event.preventDefault();
    event.stopPropagation();
  };

  public generateLinkListByLkType = (headerData: IHeaderV1Schema) => {
    const { balance, lkUrl, lkLinks, otherLinks, mortgageLinks, lkType, leadsCount } = headerData;

    const availableLinkList: IMenuLink[] = [
      {
        key: 'lk',
        title: 'Личный кабинет',
        href: lkUrl,
        availableLkTypes: null,
      },
      {
        key: 'balance',
        title: `Баланс: ${balance ? numberToPrettyString(balance) : 0} ₽`,
        icon: <BalanceIcon />,
        href: lkLinks && lkLinks.balance,
        availableLkTypes: [ELkType.Homeowner, ELkType.Specialist],
        className: 'balance',
      },
      {
        key: 'professionalSearch',
        title: 'Профпоиск',
        href: `${lkUrl}offers`,
        availableLkTypes: [ELkType.Specialist],
        clickCallback: trackProfessionalSearchClick,
      },
      {
        key: 'offers',
        title: 'Мои объявления',
        href: lkLinks && lkLinks.offers,
        availableLkTypes: [ELkType.Homeowner, ELkType.Specialist],
      },
      {
        key: 'calls',
        title: 'Звонки',
        href: lkLinks && lkLinks.calls,
      },
      {
        key: 'promoCode',
        title: 'Ввести промокод',
        href: lkUrl,
        availableLkTypes: [ELkType.Searcher, ELkType.Homeowner],
      },
      {
        key: 'addForm',
        title: 'Разместить объявление',
        href: otherLinks && otherLinks.addForm,
        availableLkTypes: [ELkType.Searcher, ELkType.Homeowner],
        disable: headerData.hasBlockedSanction,
      },
      {
        key: 'statistics',
        title: 'Отчёт о размещении',
        href: lkLinks && lkLinks.statistic,
        availableLkTypes: [ELkType.Specialist],
      },
      {
        key: 'leads',
        title: 'Заявки',
        href: lkLinks && lkLinks.leads,
        availableLkTypes: [ELkType.Specialist],
        icon: leadsCount && leadsCount > 0 ? <CountView className="user-leads">{leadsCount}</CountView> : undefined,
        className: 'leads',
      },
      {
        key: 'mortgage',
        title: 'Ипотека',
        href: mortgageLinks && mortgageLinks.anketa,
        availableLkTypes: [ELkType.Searcher],
      },
      {
        key: 'helpCenter',
        title: 'Справочный центр',
        href: otherLinks && otherLinks.helpCenter,
        availableLkTypes: null,
      },
    ];

    // Проверяем тип ЛК для которых ссылка отображается,
    // null значит для всех
    // или дополнительная проверка, если такой тип в списке доступных
    return availableLinkList
      .filter(link => link.href && (!link.availableLkTypes || (lkType && link.availableLkTypes.includes(lkType))))
      .filter(link => !link.disable);
  };

  private renderUserMenu = () => {
    const { headerData } = this.props;
    if (headerData) {
      const { userId, firstName, lastName, lkLinks } = headerData;
      const isUseNames = firstName && lastName;
      const primaryTitle = isUseNames ? `${firstName} ${lastName}` : `ID ${userId}`;
      const secondTitle = isUseNames && `ID ${userId}`;
      const availableLinkList = this.generateLinkListByLkType(headerData);

      return (
        <React.Fragment>
          <OptionalLink href={lkLinks && lkLinks.profile} className="user-name">
            {primaryTitle}
          </OptionalLink>
          {secondTitle && (
            <OptionalLink href={lkLinks && lkLinks.profile} className="user-id">
              {secondTitle}
            </OptionalLink>
          )}
          <ul className="menu-list">
            {availableLinkList.map(link => (
              <li key={link.key} {...mergeStyles('menu-list-item', link.className)}>
                <OptionalLink href={link.href} onClick={link.clickCallback}>
                  {link.title}
                  {link.icon}
                </OptionalLink>
              </li>
            ))}

            <li className="menu-list-item" id="header-user-logout">
              <OptionalLink id="logout-btn" className="menu-list-item-logout" onClick={this.logoutClick}>
                Выйти
              </OptionalLink>
            </li>
          </ul>
        </React.Fragment>
      );
    }

    return undefined;
  };

  private handleShow = (event: React.MouseEvent<HTMLDivElement>) => {
    this.setState({
      isOpen: true,
    });
  };

  private handleHide = (event: React.MouseEvent<HTMLDivElement>) => {
    this.setState({
      isOpen: false,
    });
  };

  private outSideClick = (event: React.FocusEvent<HTMLDivElement>) => {
    this.setState({
      isOpen: false,
    });
  };

  private logoutClick = (event: React.MouseEvent<HTMLElement>) => {
    const logoutBtn = document.getElementById('logout-btn');
    if (logoutBtn && window.__header_logout) {
      window.__header_logout(event);
    }

    if (window._headerTrackingEvent) {
      window._headerTrackingEvent('logout');
    }
  };

  private debouncedHandleShow = debounce(this.handleShow, 200);

  private debouncedHandleHide = debounce(this.handleHide, 200);

  private debouncedOutSideClick = debounce(this.outSideClick, 200);
}
